import moment from 'moment/moment';
import React, { useState } from 'react';

import { DateTimeField } from '@shared/forms/components/DateTimeField';
import { Form, useForm } from '@shared/forms/components/Form';
import { TextField } from '@shared/forms/components/TextField';
import { getTimeTrackingEntry } from '@shared/time-tracking-entries/endpoints/getTimeTrackingEntry';
import {
  UpdateTimeTrackingEntryRequest,
  updateTimeTrackingEntry,
} from '@shared/time-tracking-entries/endpoints/updateTimeTrackingEntry';
import { cn } from '@shared/utils/helpers';

type TimeTrackingEntry = App.Domain.TimeTracking.Data.TimeTrackingEntryData;

type Props = {
  timeTrackingEntryId: number;
  onSave?: (response: TimeTrackingEntry) => void;
  onSaveAndReturn?: () => void;
  onCancel?: () => void;
};

export function EditTimeTrackingEntryForm({ timeTrackingEntryId, onSave, onSaveAndReturn, onCancel }: Props) {
  const [timeTrackingEntry, setTimeTrackingEntry] = useState<TimeTrackingEntry | null>(null);

  const { form, fields } = useForm<UpdateTimeTrackingEntryRequest, TimeTrackingEntry>({
    source: {
      id: timeTrackingEntryId,
      provider: ({ id }) => getTimeTrackingEntry({ id }),
      onLoad: setTimeTrackingEntry,
      transform: (data) => ({
        ...data,
        startedAt: moment.utc(data.startedAt).format(),
        endedAt: data.endedAt ? moment.utc(data.endedAt).format() : '',
      }),
    },
  });

  function handleSave(values: TimeTrackingEntry) {
    onSave?.(values);
    form.reset();
  }

  return (
    <Form
      action={(data) => updateTimeTrackingEntry({ id: timeTrackingEntryId }, data)}
      onSave={handleSave}
      onSaveAndReturn={onSaveAndReturn}
      onCancel={onCancel}
      form={form}
      submitLabel="Update"
      confirmation={{
        title: 'Time Tracking Entry Updated',
        description: 'The time tracking entry has been updated successfully.',
      }}
    >
      {form.watch('startedAt')}
      <DateTimeField label="Started At" name={fields.startedAt} required />
      <fieldset
        key={timeTrackingEntry?.id}
        className={cn('grid gap-6', { 'opacity-50': !timeTrackingEntry?.endedAt })}
        disabled={!timeTrackingEntry?.endedAt}
      >
        <DateTimeField label="Ended At" name={fields.endedAt} required={!!timeTrackingEntry?.endedAt} />
      </fieldset>
    </Form>
  );
}
